<template>
  <div class="relative w-full h-full flex pr-2 min-w-0" style="max-width: 948px;"
  @mousemove="handleGraphMouseOver" @mouseleave="hoveredDataPointIndex = null">
    <!-- Y-axis labels -->
    <div class="h-full pl-2 pt-2 pb-8 pr-3 flex flex-col justify-between">
      <BaseText v-for="creditsLabel in yAxisLabels" :key="creditsLabel" type="body" size="xs" class="text-text-normal">
        {{ creditsLabel }}
      </BaseText>
    </div>
    <!-- Line Graph -->
    <div class="relative flex-grow flex flex-col h-full min-w-0">
      <div class="flex-grow" style="padding-top: 16px; padding-bottom: 15px;">
        <div ref="graphArea" class="relative w-full h-full">
          <BackgroundRenderer 
            class="absolute top-0 left-0"
            :dimensions="renderAreaDimensions" 
            :num-plot-lines="yAxisLabels.length" 
            :use-secondary-plot-lines="false"
            dash-array="8 10"
            :stroke-width="1"
            :secondary-line-padding="0"
          />
          <div class="relative w-full h-full">
            <DynamicLine
              v-if="dataCoordinates?.length && !showEmptyState"
              :dimensions="lineRenderDimensions"
              :coordinates="dataCoordinates"
              line-color="#060710eb"
              :override-filet-radius="0.33"
              @apply-marker-offsets="pointMarkerOffsets = $event"
            />
          </div>
        </div>
      </div>
      <!-- X-axis labels -->
      <!-- x positioners (render labels conditionally according to the constraints) -->
      <div class="relative w-full h-6 flex items-start justify-between px-4 min-w-0">
        <div 
          v-for="(label, index) in xAxisLabels" 
          ref="xPositioners"
          :key="`data-positioner-${index}`" 
          class="relative w-px h-full"
        >
          <BaseText v-if="index % 5 === 0" type="body" size="xs" 
          class="text-text-normal absolute left-1/2 transform -translate-x-1/2 w-max">
            {{ label }}
          </BaseText>
          <div 
            v-show="hoveredDataPointIndex === index"
            class="data-point-marker"
            :key="`data-point-marker-${index}`"
            :style="{ bottom: `${dataPointMarkers?.[index]?.yPos ?? 0}px` }"
          />
        </div>
      </div>
      <!-- Animating Hover Tooltip -->
      <transition>
        <div v-show="tooltipData" class="hover-tooltip" :style="{ transform: tooltipDisplay?.transform }">
          <UsageGraphHoverTooltip 
            :credit-count="tooltipDisplay?.credit_count"
            :date="tooltipDisplay?.date"
            :is-daily="true"
          />
        </div>
      </transition>
    </div>
    <!-- Empty State -->
    <div v-if="showEmptyState" class="absolute left-1/2 top-14 transform -translate-x-1/2 z-50
    flex flex-col items-center gap-1.5 py-4 px-6 rounded-lg bg-neutral-700 shadow-md"
    style="width: 224px;">
      <BaseText type="label" size="sm" class="text-white">
        No Usage Data
      </BaseText>
      <BaseText type="body" size="xs" class="text-neutral-alpha-700 text-center">
        No API usage for this key was detected in the past 30 days
      </BaseText>
    </div>
  </div>
</template>

<script>
import { scaleLinear } from 'd3-scale'
import moment from 'moment'

import BackgroundRenderer from '../lens/graphs/line/BackgroundRenderer.vue'
import DynamicLine from '../lens/graphs/line/DynamicLine.vue'
import UsageGraphHoverTooltip from './UsageGraphHoverTooltip.vue'

const DAY_LABEL_WIDTH = 100

const DATA_POINT_HOVER_Y_THRESHOLD = 25 // px

export default {
  name: 'ApiUsageLineGraph',
  components: {
    BackgroundRenderer,
    DynamicLine,
    UsageGraphHoverTooltip
  },
  props: {
    usageTimestamps: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      dataCoordinates: [],
      axisScale: null,
      yAxisLabels: [],
      xAxisLabels: [],
      pointMarkerOffsets: [],
      renderAreaDimensions: { width: 0, height: 0 },
      resizeObserver: null,
      maxDaysDisplayed: 30,
      hoveredDataPointIndex: null,
      tooltipDisplay: null,

      DAY_LABEL_WIDTH,
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.initializeAxisInformation()
      this.resizeObserver = new ResizeObserver(() => {
        this.computeGraphRenderArea()
        this.computeCoordinates()
      })
      this.resizeObserver.observe(this.$refs.graphArea)
    })
  },
  watch: {
    timeSeries: {
      handler () {
        this.$nextTick(() => {
          this.computeCoordinates()
        })
      },
      deep: true
    },
    tooltipData (newVal) {
      if (newVal) this.tooltipDisplay = newVal
    }
  },
  computed: {
    tooltipData () {
      if (this.hoveredDataPointIndex === null) return null
      const dataPointMarker = this.dataPointMarkers[this.hoveredDataPointIndex]
      const { xPos, yPos } = dataPointMarker
      const xStr = `calc(-50% + ${xPos}px)`
      const yStr = `calc(-24px - ${yPos}px)`

      return {
        ...dataPointMarker,
        transform: `translate(${xStr}, ${yStr})`
      }
    },
    dataPointMarkers () {
      if (!this.dataCoordinates?.length || !this.timeSeries?.length) return []
      return this.dataCoordinates.map((data, index) => {
        const offset = this.pointMarkerOffsets?.find(offset => offset.dateIndex === index)?.offset ?? 0
        return {
          ...this.timeSeries[index],
          xPos: data.x,
          yPos: this.axisScale(data.credit_count) + 34 - offset
        }
      })
    },
    timeSeries () {
      return [...this.usageTimestamps].reverse()
    },
    lineRenderDimensions () {
      if (!this.renderAreaDimensions) return { width: 0, height: 0 }
      return {
        width: this.renderAreaDimensions.width - 16,
        height: this.renderAreaDimensions.height
      }
    },
    showEmptyState () {
      return Math.max(...this.usageTimestamps?.map(timestamp => timestamp.credit_count)) === 0
    }
  },
  methods: {
    computeCoordinates () {
      const xPositioners = this.$refs.xPositioners
      const timeSeries = this.timeSeries
      if (!timeSeries?.length || xPositioners?.length !== timeSeries.length || !this.$refs.graphArea) return

      const renderAreaRect = this.$refs.graphArea.getBoundingClientRect()
      this.dataCoordinates = timeSeries.map((data, index) => ({
        ...data,
        dateIndex: index,
        x: xPositioners[index].getBoundingClientRect().left - renderAreaRect.left,
        y: this.renderAreaDimensions.height - this.axisScale(data.credit_count) - 1
      }))
    },
    initializeAxisInformation () {
      if (!this.usageTimestamps?.length) return
      this.computeGraphRenderArea()
      this.computeAxisScale()
      this.computeYAxisLabels() // Credits Labels
      this.computeXAxisLabels() // Day Labels
    },
    computeAxisScale () {
      const maxCredits = Math.max(...this.usageTimestamps.map(timestamp => timestamp.credit_count))
      this.axisScale = scaleLinear()
        .domain([0, Math.max(100, maxCredits)])
        .range([0, this.renderAreaDimensions.height])
    },
    computeYAxisLabels () {
      let ticks = this.axisScale.ticks(8)
      const reduceTicks = (ticks) => {
        // Ensures that the number of ticks is less than or equal to 8
        let tickValues = [...ticks]
        if (ticks.length % 2 === 0) {
          const newMax = ticks[ticks.length - 1] + ticks[1]
          tickValues.push(newMax)
          this.axisScale.domain([0, newMax])
        }
        while (tickValues.length > 8) {
          tickValues = tickValues.filter((_, index) => index % 2 === 0)
        }
        return tickValues
      }
      ticks = reduceTicks(ticks)

      // Ensure the highest tick covers the domain maximum
      if (ticks[ticks.length - 1] < this.axisScale.domain()[1]) {
        const newMax = ticks[ticks.length - 1] + ticks[1]
        ticks.push(newMax)
        this.axisScale.domain([0, newMax])
      }

      this.yAxisLabels = ticks.reverse().map(tick => tick.toLocaleString())
    },
    computeXAxisLabels () {
      const last31Days = []
      for (let i = 0; i < 31; i++) {
        last31Days.push(moment().subtract(i, 'day').format('MMM D'))
      }
      this.xAxisLabels = last31Days.reverse()
    },
    computeGraphRenderArea () {
      const graphArea = this.$refs.graphArea
      if (!graphArea) return
      const { width, height } = graphArea.getBoundingClientRect()
      this.renderAreaDimensions = { width, height }
      this.maxDaysDisplayed = Math.min(30, Math.floor(width / DAY_LABEL_WIDTH))
    },
    handleGraphMouseOver (event) {
      if (!this.$refs.graphArea || !this.dataCoordinates?.length || !this.dataPointMarkers?.length) {
        if (this.hoveredDataPointIndex !== null) this.hoveredDataPointIndex = null
        return
      }
      const { clientX, clientY } = event
      const renderAreaRect = this.$refs.graphArea.getBoundingClientRect()
      const mouseX = clientX - renderAreaRect.left
      const mouseY = clientY - renderAreaRect.top
      
      // Find the closest xPosition to the mouseX
      const xIndex = findClosestIndex(mouseX, this.dataCoordinates?.map(coordinate => coordinate.x))
      // Find the index of the closest data point marker to the mouseY
      const dataPoint = this.dataCoordinates?.[xIndex]
      if (dataPoint && dataPoint.y && Math.abs(mouseY - dataPoint.y) <= DATA_POINT_HOVER_Y_THRESHOLD) {
        this.hoveredDataPointIndex = xIndex
      } else {
        this.hoveredDataPointIndex = null
      }
    },
  }
}

const findClosestIndex = (toFind, toSearch) => {
  if (!toSearch?.length || toSearch.includes(undefined)) return null
  let low = 0;
  let high = toSearch.length - 1;
  if (toFind <= toSearch[low]) return low;
  if (toFind >= toSearch[high]) return high;

  // Binary search (logn) the sorted array to narrow down the range.
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    if (toSearch[mid] === toFind) return mid;
    else if (toSearch[mid] < toFind) low = mid + 1;
    else high = mid - 1;
  }

  return (toSearch[low] - toFind) < (toFind - toSearch[high]) ? low : high;
}
</script>

<style scoped>
.data-point-marker {
  position: absolute;
  left: 0.25px;
  transform: translate(-50%, -50%);
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #060710eb;
}
.hover-tooltip {
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 40001;

  transition: transform 200ms ease-in-out;
  transform-origin: top left;
}

.v-enter-active, .v-leave-active {
  transition: opacity 200ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>